import { RefObject, useCallback, useState } from "react";
import useSpaces from "hooks/useSpaces";
import { createMarker } from "utils/markerUtils";

const usePersonalSpaces = (map: RefObject<mapboxgl.Map | null>) => {
	const spaces = {
		home: null,
		work: null,
		school: null,
		gym: null,
		worship: null,
	};

	const [personalSpaceMarkers, setPersonalSpaceMarkers] = useState<{
		[key: string]: mapboxgl.Marker | null;
	}>(spaces);

	const [markerPositions, setMarkerPositions] = useState<{
		[key: string]: mapboxgl.LngLat | null;
	}>(spaces);

	const { personalSpaces } = useSpaces();

	const handlePersonalSpaceMarkers = useCallback(
		(spaceId: string) => {
			// If this space already has a marker, remove it and clear its position
			if (personalSpaceMarkers[spaceId]) {
				personalSpaceMarkers[spaceId]?.remove();

				setPersonalSpaceMarkers((prev) => ({
					...prev,
					[spaceId]: null,
				}));

				setMarkerPositions((prev) => ({
					...prev,
					[spaceId]: null,
				}));

				return;
			}

			// Change cursor to indicate placement mode
			if (map && map.current) {
				map.current.getCanvas().style.cursor = "crosshair";

				// Add one-time click handler for map
				const clickHandler = (e: mapboxgl.MapMouseEvent) => {
					const spaceConfig = personalSpaces[spaceId];

					if (!spaceConfig?.label || !spaceConfig?.icon || !spaceConfig?.color)
						return;

					const marker = createMarker(spaceConfig, e.lngLat, map.current!);

					// Store marker position
					setMarkerPositions((prev) => ({
						...prev,
						[spaceId]: e.lngLat,
					}));

					// Add dragend event listener to update position
					marker.on("dragend", () => {
						const newPosition = marker.getLngLat();
						setMarkerPositions((prev) => ({
							...prev,
							[spaceId]: newPosition,
						}));
					});

					// Store the marker
					setPersonalSpaceMarkers((prev) => ({
						...prev,
						[spaceId]: marker,
					}));

					// Reset cursor
					map.current!.getCanvas().style.cursor = "";

					try {
						map.current?.off("click", clickHandler);
					} catch (error) {
						console.error(
							"Error removing click event listener for marker:",
							(error as Error).message
						);
					}
				};

				map.current.once("click", clickHandler);
			}
		},
		[personalSpaceMarkers, personalSpaces, map]
	);

	return {
		handlePersonalSpaceMarkers,
		personalSpaceMarkers,
		markerPositions,
	};
};

export default usePersonalSpaces;
